import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import {
  CURRENT_PHONE,
  CURRENT_SEASON_NUMBER,
  HOTSTAR_URL,
  season10Contestants,
  season9ImageMap,
  seasonArchives,
} from "./data/seasons";
import { articleCategories, newsArticles, refreshNewsFromGoogleInBrowser } from "./data/news";

const STRAWPOLL_EMBED_URL = "https://strawpoll.com/embed/kogjR52P3g6";
const FALLBACK_IMAGE = "https://placehold.co/720x960/101323/F5F7FF?text=Bigg+Boss+Telugu";

const routes = {
  home: "/",
  vote: "/vote",
  contestants: "/contestants",
  seasons: "/seasons",
  news: "/news",
  guide: "/how-to-vote",
};

function currentPath() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path;
}

function navigate(path) {
  if (currentPath() === path) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function Link({ to, children, className = "", onClick }) {
  const handleClick = (event) => {
    event.preventDefault();
    navigate(to);
    onClick?.();
  };

  return (
    <a className={className} href={to} onClick={handleClick}>
      {children}
    </a>
  );
}

function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const navItems = [
    [routes.home, "Home"],
    [routes.vote, "Live Vote"],
    [routes.contestants, "Contestants"],
    [routes.news, "News & Articles"],
    [routes.seasons, "Season Archive"],
    [routes.guide, "How to Vote"],
  ];

  return (
    <div className="site-shell">
      <header className="topbar">
        <div className="topbar__inner">
          <Link to={routes.home} className="brand" aria-label="Bigg Boss Telugu home">
            <span className="brand__mark">BB</span>
            <span><strong>BIGG BOSS</strong><em>TELUGU</em></span>
          </Link>

          <nav className="topnav" aria-label="Primary navigation">
            {navItems.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
          </nav>

          <div className="topbar__actions">
            <Link className="topbar__cta shine" to={routes.vote}>Vote Now</Link>
            <button
              className={`menu-toggle ${menuOpen ? "is-open" : ""}`}
              type="button"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        <div id="mobile-menu" className={`mobile-menu ${menuOpen ? "is-open" : ""}`}>
          {navItems.map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)}>{label}</Link>
          ))}
          <Link className="mobile-menu__vote shine" to={routes.vote} onClick={() => setMenuOpen(false)}>Open Live Voting</Link>
        </div>
      </header>

      {children}

      <footer className="footer">
        <div className="footer__inner">
          <div>
            <Link className="brand brand--footer" to={routes.home}>
              <span className="brand__mark">BB</span><span><strong>BIGG BOSS</strong><em>TELUGU</em></span>
            </Link>
            <p>Independent Bigg Boss Telugu fan destination for voting information, contestant profiles, news and season history.</p>
          </div>
          <div className="footer__links">
            {navItems.slice(1).map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
          </div>
        </div>
        <div className="footer__legal">
          <small>Disclaimer: This is not the official Bigg Boss Telugu website. Bigg Boss, Star Maa, JioHotstar and related marks belong to their respective owners. News is independently curated and linked to original publishers.</small>
          <span>© {new Date().getFullYear()} ZINZER Labs</span>
        </div>
      </footer>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, action }) {
  return (
    <section className="page-header">
      <div className="dust-layer" aria-hidden="true" />
      <div className="page-header__inner">
        <div>
          <span className="section-label">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {action}
      </div>
    </section>
  );
}

function HomePage() {
  const featured = newsArticles.slice(0, 3);
  return (
    <main>
      <section className="hero-section">
        <div className="dust-layer" aria-hidden="true" />
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="kicker">SEASON {CURRENT_SEASON_NUMBER} • DASAVATHARAM</p>
            <h1>Bigg Boss Telugu, designed like the show deserves.</h1>
            <p className="hero-lead">Live voting, contestant profiles, breaking house news, season archives and fan polls in one cinematic destination.</p>
            <div className="hero-actions">
              <Link className="hero-button hero-button--primary shine" to={routes.vote}>Vote Now</Link>
              <Link className="hero-button hero-button--ghost" to={routes.contestants}>Meet the Housemates</Link>
            </div>
            <div className="hero-meta">
              <div><strong>16</strong><span>Housemates</span></div>
              <div><strong>10</strong><span>Current season</span></div>
              <div><strong>24×7</strong><span>Streaming ecosystem</span></div>
            </div>
          </div>
          <aside className="hero-card">
            <span className="hero-card__tag">LIVE FAN HUB</span>
            <div className="hero-card__orb">10</div>
            <h2>Season 10 is live.</h2>
            <p>Jump from the latest house buzz to voting without digging through a long single-page feed.</p>
            <Link className="mini-link shine" to={routes.vote}>Open live voting →</Link>
          </aside>
        </div>
      </section>

      <section className="content-section spotlight-section">
        <div className="section-heading section-heading--compact">
          <div><span className="section-label">TODAY'S HUB</span><h2>Everything fans come back for</h2></div>
          <Link className="text-link" to={routes.news}>See all news →</Link>
        </div>
        <div className="spotlight-grid">
          <Link className="spotlight-card spotlight-card--vote" to={routes.vote}>
            <span>01</span><strong>Live Vote</strong><p>Open the fan poll, see results and continue to official voting.</p><b>Vote now →</b>
          </Link>
          <Link className="spotlight-card" to={routes.contestants}>
            <span>02</span><strong>Contestant Profiles</strong><p>Meet the Season 10 housemates, explore their profiles and find voting options.</p><b>Meet everyone →</b>
          </Link>
          <Link className="spotlight-card" to={routes.news}>
            <span>03</span><strong>News & Articles</strong><p>House buzz, nominations, eliminations, interviews, explainers and opinion.</p><b>Read the latest →</b>
          </Link>
        </div>
      </section>

      <section className="content-section latest-section">
        <div className="section-heading">
          <div><span className="section-label">LATEST STORIES</span><h2>What's happening inside the house</h2></div>
          <p>Catch the latest Bigg Boss Telugu updates as they develop.</p>
        </div>
        <div className="article-grid">
          {featured.map((article) => <ArticleCard key={article.id} article={article} />)}
        </div>
      </section>

      <section className="content-section season-banner">
        <div><span className="section-label">THE ARCHIVE</span><h2>Ten seasons. One Telugu Bigg Boss timeline.</h2><p>Explore winners, runners-up, hosts, themes and contestant histories from Season 1 through Season 9.</p></div>
        <Link className="hero-button hero-button--ghost" to={routes.seasons}>Explore seasons →</Link>
      </section>
    </main>
  );
}

function PollPage() {
  return (
    <main>
      <PageHeader eyebrow="LIVE POLL" title="Bigg Boss Telugu 10 Fan Voting" description="Vote in the independent fan poll, watch the audience trend, then use JioHotstar for the official voting experience." action={<Link className="hero-button hero-button--primary shine" to={routes.contestants}>View contestants</Link>} />
      <section className="content-section voting-section">
        <div className="poll-shell">
          <div className="poll-shell__topline"><span><i className="live-dot" /> Live fan poll</span><span>Unofficial audience sentiment</span></div>
          <div className="embed-wrap"><iframe title="Bigg Boss Telugu Season 10 live fan poll" src={STRAWPOLL_EMBED_URL} frameBorder="0" sandbox="allow-scripts allow-same-origin allow-forms" loading="lazy" /></div>
        </div>
        <div className="support-panel">
          <div><span className="section-label">OFFICIAL ROUTE</span><h2>Use JioHotstar when official voting opens.</h2><p>Fan poll results on this website are informational and do not submit official votes.</p></div>
          <a className="action-button action-button--hotstar shine" href={HOTSTAR_URL} target="_blank" rel="noopener noreferrer"><img src="https://secure-media.hotstar.com/web-assets/prod/jhs_favicon.ico" alt="" aria-hidden="true" />Vote on JioHotstar</a>
        </div>
      </section>
    </main>
  );
}

function ContestantCard({ contestant }) {
  return (
    <article className="contestant-card">
      <Link to={`/contestants/${contestant.id}`} className="contestant-card__media">
        <img src={contestant.img || FALLBACK_IMAGE} alt={`${contestant.name} - Bigg Boss Telugu Season 10 contestant`} loading="lazy" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
        <span className="contestant-card__season">BB {CURRENT_SEASON_NUMBER}</span>
      </Link>
      <div className="contestant-card__body">
        <div className="contestant-card__eyebrow">HOUSEMATE</div>
        <Link to={`/contestants/${contestant.id}`} className="card-title-link"><h2>{contestant.name}</h2></Link>
        <p className="contestant-card__role">{contestant.profession}</p>
        <p className="contestant-card__bio">{contestant.bio}</p>
        <div className="vote-actions" aria-label={`Voting options for ${contestant.name}`}>
          <a className="action-button action-button--hotstar shine" href={HOTSTAR_URL} target="_blank" rel="noopener noreferrer"><img src="https://secure-media.hotstar.com/web-assets/prod/jhs_favicon.ico" alt="" aria-hidden="true" />Vote on JioHotstar</a>
          <a className="action-button action-button--call shine" href={`tel:${CURRENT_PHONE}`}><span aria-hidden="true">☎</span> Missed Call Vote</a>
        </div>
      </div>
    </article>
  );
}

function ContestantsPage() {
  return (
    <main>
      <PageHeader eyebrow="SEASON 10" title="Bigg Boss Telugu 10 Contestants" description="Profiles, professions, bios and direct voting actions." action={<Link className="hero-button hero-button--primary shine" to={routes.vote}>Live poll</Link>} />
      <section className="content-section contestants-section">
        <div className="contestant-grid">{season10Contestants.map((contestant) => <ContestantCard key={contestant.id} contestant={contestant} />)}</div>
      </section>
    </main>
  );
}

function ContestantProfile({ contestant }) {
  return (
    <main>
      <PageHeader eyebrow={`SEASON ${CURRENT_SEASON_NUMBER} • PROFILE`} title={contestant.name} description={`${contestant.profession}. ${contestant.bio}`} action={<Link className="hero-button hero-button--ghost" to={routes.contestants}>← All contestants</Link>} />
      <section className="content-section profile-layout">
        <div className="profile-image-wrap"><img src={contestant.img || FALLBACK_IMAGE} alt={`${contestant.name} profile`} referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} /></div>
        <div className="profile-copy">
          <span className="section-label">HOUSEMATE PROFILE</span>
          <h2>{contestant.name}</h2>
          <p className="profile-role">{contestant.profession}</p>
          <p>{contestant.bio}</p>
          <div className="profile-actions"><a className="action-button action-button--hotstar shine" href={HOTSTAR_URL} target="_blank" rel="noopener noreferrer"><img src="https://secure-media.hotstar.com/web-assets/prod/jhs_favicon.ico" alt="" aria-hidden="true" />Vote on JioHotstar</a><a className="action-button action-button--call shine" href={`tel:${CURRENT_PHONE}`}>☎ Missed Call Vote</a></div>
        </div>
      </section>
    </main>
  );
}

function withArchiveImages(name) {
  return season9ImageMap[name] || `https://placehold.co/720x960/101323/F5F7FF?text=${encodeURIComponent(name)}`;
}

function SeasonsPage() {
  const [selectedSeason, setSelectedSeason] = useState(9);
  const season = seasonArchives.find((item) => item.number === selectedSeason) || seasonArchives[seasonArchives.length - 1];
  return (
    <main>
      <PageHeader eyebrow="THE ARCHIVE" title="Bigg Boss Telugu Seasons 1–9" description="Explore winners, runners-up, hosts, themes and contestant histories from each season." />
      <section className="content-section archive-section">
        <div className="season-grid">{seasonArchives.map((item) => <button key={item.number} type="button" className={`season-card ${selectedSeason === item.number ? "is-active" : ""}`} onClick={() => setSelectedSeason(item.number)}><span>SEASON {item.number}</span><strong>{item.year}</strong><small>{item.theme}</small><b>{item.winner}</b><em>Winner</em></button>)}</div>
        <div className="archive-detail">
          <div className="archive-detail__header"><div><span className="section-label">SEASON {season.number}</span><h2>{season.title}</h2><p>{season.days} days • Host: {season.host} • Theme: {season.theme}</p></div></div>
          <div className="archive-winners"><div><span>WINNER</span><strong>{season.winner}</strong></div><div><span>RUNNER-UP</span><strong>{season.runnerUp}</strong></div></div>
          <div className="archive-contestant-grid">{season.contestants.map((name, index) => <article key={`${season.number}-${name}-${index}`} className="archive-person"><img src={withArchiveImages(name)} alt={`${name} - Bigg Boss Telugu Season ${season.number}`} loading="lazy"/><div><strong>{name}</strong><span>{name === season.winner ? "Winner" : name === season.runnerUp ? "Runner-up" : "Contestant"}</span></div></article>)}</div>
        </div>
      </section>
    </main>
  );
}

function ArticleCard({ article }) {
  const image = article.image || null;
  return (
    <article className="article-card">
      <Link to={`/news/${article.slug}`} className="article-card__media">
        {image ? <img src={image} alt="" loading="lazy" onError={(e) => { e.currentTarget.removeAttribute("src"); }} /> : <span className="article-card__media-fallback" aria-hidden="true" />}
      </Link>
      <div className="article-card__body"><div className="article-card__meta"><span>{article.category}</span><time dateTime={article.date}>{article.date}</time></div><Link to={`/news/${article.slug}`} className="card-title-link"><h3>{article.title}</h3></Link><p>{article.excerpt}</p><div className="article-card__footer"><span>{article.source}</span><Link to={`/news/${article.slug}`}>Read article →</Link></div></div>
    </article>
  );
}

function NewsPage() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [browserNews, setBrowserNews] = useState(newsArticles);
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");

  useEffect(() => {
    const onNewsUpdated = (event) => {
      if (Array.isArray(event.detail) && event.detail.length) setBrowserNews(event.detail);
    };
    window.addEventListener("bigg-boss-news-updated", onNewsUpdated);
    return () => window.removeEventListener("bigg-boss-news-updated", onNewsUpdated);
  }, []);

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    const searchText = query.trim();
    setSearchMessage("");

    if (!searchText) {
      setSearching(true);
      const latest = await refreshNewsFromGoogleInBrowser();
      if (Array.isArray(latest) && latest.length) setBrowserNews(latest);
      else setBrowserNews(newsArticles);
      setSearching(false);
      return;
    }

    setSearching(true);
    const results = await refreshNewsFromGoogleInBrowser(searchText);
    if (Array.isArray(results) && results.length) {
      setBrowserNews(results);
      setSearchMessage(`${results.length} latest stories for “${searchText}”`);
    } else {
      setSearchMessage(`No fresh stories found for “${searchText}”. Showing the current feed.`);
    }
    setSearching(false);
  };

  const filtered = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    return browserNews.filter((article) =>
      (category === "All" || article.category === category) &&
      (!lowered || `${article.title} ${article.excerpt}`.toLowerCase().includes(lowered))
    );
  }, [browserNews, category, query]);

  return (
    <main>
      <PageHeader eyebrow="EDITORIAL DESK" title="Bigg Boss Telugu News & Articles" description="The latest house buzz, nominations, eliminations, interviews and fan-focused stories." action={<Link className="hero-button hero-button--primary shine" to={routes.vote}>Vote now</Link>} />
      <section className="content-section news-section">
        <form className="news-controls" onSubmit={handleSearchSubmit}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Bigg Boss news…" aria-label="Search news" />
          <button type="submit" disabled={searching}>{searching ? "Searching…" : "Search"}</button>
          <div className="category-tabs">{articleCategories.map((item) => <button key={item} type="button" className={category === item ? "is-active" : ""} onClick={() => setCategory(item)}>{item}</button>)}</div>
        </form>
        {searchMessage && <p className="news-search-message" role="status">{searchMessage}</p>}
        <div className="article-grid">{filtered.map((article) => <ArticleCard key={article.id} article={article}/>)}{filtered.length === 0 && <div className="empty-state">No matching stories yet.</div>}</div>
      </section>
    </main>
  );
}

function ArticlePage({ article }) {
  const paragraphs = String(article.excerpt || "").split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);

  return (
    <main>
      <PageHeader eyebrow={`${article.category} • ${article.source}`} title={article.title} description={article.date} action={<Link className="hero-button hero-button--ghost" to={routes.news}>← All news</Link>} />
      <article className="content-section article-detail">
        {article.image ? <img className="article-detail__hero" src={article.image} alt="" onError={(e) => { e.currentTarget.removeAttribute("src"); }} /> : null}
        <div className="article-detail__content">
          <div className="article-body">
            {paragraphs.map((paragraph, index) => <p key={`${article.slug}-${index}`}>{paragraph}</p>)}
          </div>
          <a className="source-button" href={article.sourceUrl} target="_blank" rel="noopener noreferrer">Read more</a>
          {article.videoId && <div className="video-embed"><iframe title={`${article.title} video`} src={`https://www.youtube.com/embed/${article.videoId}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen/></div>}
        </div>
      </article>
    </main>
  );
}

function GuidePage() {
  const steps = ["Check the current nomination race.", "Use the fan poll here to see community sentiment.", "Open JioHotstar for official voting when the window is live.", "Return for weekly nominations, results, eliminations and house updates."];
  return (
    <main>
      <PageHeader eyebrow="VOTING GUIDE" title="How to vote for Bigg Boss Telugu" description="A simple path from the nomination list to the official voting destination, without mixing unofficial fan results with broadcaster voting." action={<Link className="hero-button hero-button--primary shine" to={routes.vote}>Open poll</Link>} />
      <section className="content-section how-section"><div className="steps-grid">{steps.map((step, index) => <div key={step}><span>0{index + 1}</span><h2>{["Check nominations", "Pick a favourite", "Use JioHotstar", "Track the trend"][index]}</h2><p>{step}</p></div>)}</div><div className="support-panel support-panel--dark"><div><span className="section-label">CALL OPTION</span><h2>Missed-call voting shortcut</h2><p>The existing call number is preserved in every current Season 10 contestant profile.</p></div><a className="action-button action-button--call shine" href={`tel:${CURRENT_PHONE}`}>☎ {CURRENT_PHONE}</a></div></section>
    </main>
  );
}

function App() {
  const [path, setPath] = useState(currentPath());
  useEffect(() => {
    const onChange = () => setPath(currentPath());
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);

  let page;
  if (path === routes.home) page = <HomePage />;
  else if (path === routes.vote) page = <PollPage />;
  else if (path === routes.contestants) page = <ContestantsPage />;
  else if (path.startsWith("/contestants/")) {
    const id = path.split("/")[2];
    const contestant = season10Contestants.find((item) => item.id === id);
    page = contestant ? <ContestantProfile contestant={contestant} /> : <ContestantsPage />;
  } else if (path === routes.seasons) page = <SeasonsPage />;
  else if (path === routes.news) page = <NewsPage />;
  else if (path.startsWith("/news/")) {
    const slug = path.split("/").slice(2).join("/");
    const article = newsArticles.find((item) => item.slug === slug);
    page = article ? <ArticlePage article={article} /> : <NewsPage />;
  } else if (path === routes.guide) page = <GuidePage />;
  else page = <HomePage />;

  return <Layout>{page}</Layout>;
}

export default App;
